<template>
  <div
    class="ProgressCircular"
    :class="{ 'is-indeterminate': indeterminate }"
    :style="containerStyle"
  >
    <svg v-if="indeterminate" class="ProgressCircular__svg" viewBox="0 0 50 50" :style="svgStyle">
      <circle
        class="ProgressCircular__circle"
        cx="25"
        cy="25"
        r="20"
        fill="none"
        :stroke="color"
        :stroke-width="width"
        stroke-linecap="round"
      />
    </svg>
    <svg v-else class="ProgressCircular__svg" viewBox="0 0 50 50" :style="svgStyle">
      <circle
        class="ProgressCircular__circle-bg"
        cx="25"
        cy="25"
        r="20"
        fill="none"
        :stroke="bgColor"
        :stroke-width="width"
      />
      <circle
        class="ProgressCircular__circle-progress"
        cx="25"
        cy="25"
        r="20"
        fill="none"
        :stroke="color"
        :stroke-width="width"
        stroke-linecap="round"
        :stroke-dasharray="circumference"
        :stroke-dashoffset="offset"
        transform="rotate(-90 25 25)"
      />
    </svg>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    size?: number | string
    width?: number
    color?: string
    bgColor?: string
    value?: number
    indeterminate?: boolean
  }>(),
  {
    size: 50,
    width: 4,
    color: 'rgb(var(--v-theme-main-blue))',
    bgColor: 'rgba(0, 0, 0, 0.1)',
    value: 0,
    indeterminate: false,
  },
)

const sizeValue = computed(() => {
  return typeof props.size === 'number' ? `${props.size}px` : props.size
})

const containerStyle = computed(() => ({
  width: sizeValue.value,
  height: sizeValue.value,
}))

const svgStyle = computed(() => ({
  width: sizeValue.value,
  height: sizeValue.value,
}))

const circumference = computed(() => {
  const radius = 20
  return 2 * Math.PI * radius
})

const offset = computed(() => {
  const progress = Math.max(0, Math.min(100, props.value))
  return circumference.value - (progress / 100) * circumference.value
})
</script>

<style scoped>
.ProgressCircular {
  display: inline-block;
  position: relative;
}

.ProgressCircular__svg {
  display: block;
  animation: rotate 2s linear infinite;
}

.ProgressCircular.is-indeterminate .ProgressCircular__circle {
  stroke-dasharray: 1, 200;
  stroke-dashoffset: 0;
  animation: dash 1.5s ease-in-out infinite;
}

.ProgressCircular__circle-bg {
  opacity: 0.3;
}

.ProgressCircular__circle-progress {
  transition: stroke-dashoffset 0.3s ease;
}

@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 200;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 89, 200;
    stroke-dashoffset: -35px;
  }
  100% {
    stroke-dasharray: 89, 200;
    stroke-dashoffset: -124px;
  }
}
</style>
